import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { Button } from '@/components/ui/Button';
import { Loader2 } from 'lucide-react';

export interface DeletePlayEventConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  /** Event detail page vs calendar session drawer */
  context?: 'event' | 'session';
  eventName?: string;
}

export function DeletePlayEventConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  context = 'event',
  eventName,
}: DeletePlayEventConfirmDialogProps) {
  const isSession = context === 'session';
  const title = isSession ? 'Delete this session?' : 'Delete this event?';
  const keepLabel = isSession ? 'Keep session' : 'Keep event';
  const confirmLabel = isSession ? 'Delete session' : 'Delete event';

  return (
    <AlertDialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              {eventName ? (
                <p>
                  <strong className="text-foreground">{eventName}</strong> will be permanently
                  removed.
                </p>
              ) : null}
              <p>
                This deletes the {isSession ? 'session' : 'event'} from the calendar and operator
                dashboard. Court holds are released. This action cannot be undone.
              </p>
              <p>Online payments already collected are not refunded automatically.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={loading}>
            {keepLabel}
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={loading}
            onClick={() => void onConfirm()}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Deleting…
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
